import { Component, Inject, OnDestroy } from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
	MAT_MOMENT_DATE_ADAPTER_OPTIONS,
	MomentDateAdapter,
} from '@angular/material-moment-adapter';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';
import moment from 'moment';

import { BeneficiaryDependentUpdateDto } from '../../../../../modules/beneficiaries/dtos/beneficiary-dependent-update';
import { TypeKinship } from '../../../../../shared/enums/type-kinship.enum';
import { DataTypeUtils } from '../../../../../utils/data-type.util';
import { PT_BR_DATE_FORMATS } from '../../../../../utils/date-formats.utils';
import { PhoneUtils } from '../../../../../utils/phone-utils';
import { ProductsConfigHelper } from '../../../../products/products.config';
import { ProductsSummary } from '../../../models/products.model';
import {
	fetchCpfDependentStart,
	fetchEmailDependentStart,
} from '../../../state/organizations.actions';
import {
	selectCpfDependent,
	selectEmailDependent,
} from '../../../state/organizations.selector';

@Component({
	providers: [
		{ provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
		{
			deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
			provide: DateAdapter,
			useClass: MomentDateAdapter,
		},
		{ provide: MAT_DATE_FORMATS, useValue: PT_BR_DATE_FORMATS },
	],
	selector: 'app-manage-dependent-modal',
	styles: [
		`
			.col-1 {
				width: 346px;
				min-width: 346px;
				&.dyn {
					min-width: 0;
				}
			}
			.col-2 {
				width: 708px;
				min-width: 708px;
				&.dyn {
					min-width: 0;
				}
			}
			.gray {
				color: #626262;
			}
			.mb-fix {
				margin-bottom: 84px;
			}
			@media only screen and (max-width: 1024px) {
				.line {
					flex-direction: column;
					align-items: flex-start;
				}
				.col-1,
				.col-2 {
					width: 95%;
					min-width: 95%;
				}
			}
		`,
	],
	templateUrl: './manage-dependent-modal.component.html',
})
export class ManageDependentModalComponent implements OnDestroy {
	cpfPattern = '[0-9]{11}';
	dependentTypes = [
		{ label: 'Cônjuge', value: TypeKinship.CONJUGE },
		{ label: 'Filho', value: TypeKinship.FILHO },
		{ label: 'Enteado', value: TypeKinship.ENTEADO },
		{ label: 'Pai / Mãe', value: TypeKinship.PAI_MAE },
		{ label: 'Neto', value: TypeKinship.NETO },
		{ label: 'Irmão', value: TypeKinship.IRMAO },
		{ label: 'Avô / Avó', value: TypeKinship.AVO },
		{ label: 'Tio / Tia', value: TypeKinship.TIO },
		{ label: 'Sobrinho', value: TypeKinship.SOBRINHO },
		{ label: 'Bisneto', value: TypeKinship.BISNETO },
		{ label: 'Sogro', value: TypeKinship.SOGRO },
		{ label: 'Genro / Nora', value: TypeKinship.GENRO_NORA },
		{ label: 'Padrasto / Madrasta', value: TypeKinship.PADRASTO_MADRASTA },
	];

	editing = -1;
	form: UntypedFormGroup;

	genders = ['Masculino', 'Feminino'];

	inputId: string = String(Math.random());

	loading = false;

	phoneMask = (value: string): string => {
		this.form.get('phone')?.updateValueAndValidity();

		return value ? PhoneUtils.phoneMask(value) : '';
	};

	products: ProductsSummary[] = [];

	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly fb: UntypedFormBuilder,
		private readonly store: Store,
		private readonly productsConfigHelper: ProductsConfigHelper,
		public dialogRef: MatDialogRef<ManageDependentModalComponent>,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			cpf: string;
			dep: BeneficiaryDependentUpdateDto;
			editing: number;
			orgId: string;
			products: ProductsSummary[];
		},
	) {
		this.editing = data.editing;
		this.products = data.products;
		this.form = this.fb.group({
			birthday: [data.dep ? data.dep.birthday : null, [Validators.required]],
			cpf: [
				data.dep ? data.dep.cpf : null,
				[Validators.required, Validators.pattern(this.cpfPattern)],
			],
			cpf_titular: [
				{ disabled: true, value: data.cpf },
				[Validators.required, Validators.pattern(this.cpfPattern)],
			],
			email: [data.dep ? data.dep.email : null, [Validators.email]],
			gender: [data.dep ? data.dep.gender : null, []],
			inclusion_day: [data.dep ? data.dep.inclusion_day : null, []],
			marriageDate: [
				data.dep ? data.dep.marriageDate : null,
				[Validators.required],
			],
			mothers_name: [data.dep ? data.dep.mothers_name : null, []],
			name: [data.dep ? data.dep.name : null, [Validators.required]],
			phone: [
				data.dep ? data.dep.phone : null,
				[Validators.pattern(DataTypeUtils.phone.pattern)],
			],
			type: [data.dep ? data.dep.type : null, [Validators.required]],
		});
		this.form.get('marriageDate')?.disable();
		if (this.form.get('type')?.value === TypeKinship.CONJUGE) {
			this.form.get('marriageDate')?.enable();
		}

		if (this.data.dep?.marriageDate) {
			this.form.patchValue({
				marriageDate: this.data.dep.marriageDate,
			});
		}
	}

	closeModal() {
		this.dialogRef.close(0);
	}

	getRequiredAttributesDependent(products: ProductsSummary[]): string[] {
		const dependentAttributes: string[] = [];
		products.forEach((product) => {
			if (product.id) {
				const productConfig = this.productsConfigHelper.findById(product.id);

				if (productConfig?.requiredAttributesDependent) {
					dependentAttributes.push(
						...productConfig.requiredAttributesDependent,
					);
				}
			}
		});

		return dependentAttributes;
	}

	getRequiredAttributesOwner(products: ProductsSummary[]): string[] {
		const ownerAttributes: string[] = [];
		products.forEach((product) => {
			if (product.id) {
				const productConfig = this.productsConfigHelper.findById(product.id);

				if (productConfig?.requiredAttributesOwner) {
					ownerAttributes.push(...productConfig.requiredAttributesOwner);
				}
			}
		});

		return ownerAttributes;
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	onContinue(): void {
		if (this.form.valid) {
			this.form.get('cpf_titular')!.enable();
			const request = this.form.value;
			request.birthday = moment(request.birthday).format(
				'YYYY-MM-DD hh:mm:ssZZ',
			);
			request.marriageDate = request.marriageDate
				? moment(request.marriageDate).format('YYYY-MM-DD hh:mm:ssZZ')
				: null;
			request.inclusion_day = request.inclusion_day
				? moment(request.inclusion_day).format('YYYY-MM-DD hh:mm:ssZZ')
				: null;
			this.form.get('cpf_titular')!.disable();
			this.dialogRef.close({ idx: this.editing, request });
		}
	}

	onCpfDependentBlur(): void {
		if (this.form.get('cpf')!.valid) {
			this.store.dispatch(
				fetchCpfDependentStart({
					cpf: this.form.get('cpf')!.value,
					orgId: this.data.orgId,
				}),
			);
			this.store
				.select(selectCpfDependent)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((result) => {
					if (result) {
						this.form.get('cpf')!.updateValueAndValidity();
					} else if (result === false) {
						this.form.get('cpf')!.setErrors({ minlength: true });
					}
				});
		}
	}

	onEmailBlur(): void {
		if (this.form.get('email')?.value && this.form.get('email')!.valid) {
			this.store.dispatch(
				fetchEmailDependentStart({
					email: this.form.get('email')!.value,
					orgId: this.data.orgId,
				}),
			);
			this.store
				.select(selectEmailDependent)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((result) => {
					if (result) {
						this.form.get('email')!.updateValueAndValidity();
					} else if (result === false) {
						this.form.get('email')!.setErrors({ minlength: true });
					}
				});
		}
	}

	onTypeDependentChange(typeKinship: TypeKinship): void {
		if (typeKinship === TypeKinship.CONJUGE) {
			this.form.get('marriageDate')?.enable();
		} else {
			this.form.get('marriageDate')?.disable();
			this.form.get('marriageDate')?.setValue(null);
		}
	}

	updateValidation(): void {
		for (const control in this.form.controls) {
			this.form.get(control)!.markAsTouched();
			this.form.get(control)!.updateValueAndValidity({ onlySelf: true });
		}
	}
}
